import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Edit, Trash2, Save, Plus, X, Image, FileText, Sparkles, ChevronDown, ChevronUp, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import ImagePicker from '../components/admin/ImagePicker';
import { adminFetch } from '../services/adminFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.mt-productions.co.uk';

const getToken = () => localStorage.getItem('adminToken');

const AboutManager = () => {
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('team');
  const [editingMember, setEditingMember] = useState(null);
  const [editingValue, setEditingValue] = useState(null);
  const [editingContent, setEditingContent] = useState(false);
  const [contentForm, setContentForm] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchAbout = async () => {
    try {
      const res = await fetch(`${API_URL}/api/about`);
      if (res.ok) {
        const data = await res.json();
        setAboutData(data);
        setContentForm({
          heroTitle: data.heroTitle || '',
          heroSubtitle: data.heroSubtitle || '',
          missionText: data.missionText || '',
          screenToStageTitle: data.screenToStageTitle || '',
          screenToStageText: data.screenToStageText || '',
          ctaTitle: data.ctaTitle || '',
          ctaText: data.ctaText || '',
        });
      }
    } catch (err) {
      toast.error('Failed to load about data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAbout(); }, []);

  const saveTeamMember = async (member, isNew = false) => {
    setSaving(true);
    try {
      const url = isNew
        ? `${API_URL}/api/admin/about/team`
        : `${API_URL}/api/admin/about/team/${member.id}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await adminFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify(member),
      });
      if (res.ok) {
        toast.success(isNew ? 'Team member added' : 'Team member updated');
        setEditingMember(null);
        fetchAbout();
      } else {
        toast.error('Failed to save');
      }
    } catch (err) {
      toast.error('Error saving team member');
    } finally {
      setSaving(false);
    }
  };

  const deleteTeamMember = async (id) => {
    if (!window.confirm('Delete this team member?')) return;
    try {
      const res = await adminFetch(`${API_URL}/api/admin/about/team/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      if (res.ok) {
        toast.success('Team member deleted');
        fetchAbout();
      }
    } catch (err) {
      toast.error('Error deleting');
    }
  };

  const saveValue = async (value) => {
    setSaving(true);
    try {
      const res = await adminFetch(`${API_URL}/api/admin/about/values/${value.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify(value),
      });
      if (res.ok) {
        toast.success('Value updated');
        setEditingValue(null);
        fetchAbout();
      }
    } catch (err) {
      toast.error('Error saving value');
    } finally {
      setSaving(false);
    }
  };

  const saveCutAround = async (cutaround) => {
    setSaving(true);
    try {
      const res = await adminFetch(`${API_URL}/api/admin/about/cutarounds/${cutaround.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify(cutaround),
      });
      if (res.ok) {
        toast.success('Cut-around updated');
        fetchAbout();
      }
    } catch (err) {
      toast.error('Error saving cut-around');
    } finally {
      setSaving(false);
    }
  };

  const saveContent = async () => {
    setSaving(true);
    try {
      const res = await adminFetch(`${API_URL}/api/admin/about/content`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify(contentForm),
      });
      if (res.ok) {
        toast.success('Content updated');
        setEditingContent(false);
        fetchAbout();
      }
    } catch (err) {
      toast.error('Error saving content');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-white/60 text-center py-12">Loading...</div>;
  if (!aboutData) return <div className="text-white/60 text-center py-12">No about data found</div>;

  const tabs = [
    { id: 'team', label: 'Team Members', icon: Users },
    { id: 'values', label: 'Values', icon: Sparkles },
    { id: 'cutarounds', label: 'Cut-Arounds', icon: Image },
    { id: 'content', label: 'Page Content', icon: FileText },
  ];

  return (
    <div data-testid="about-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-white">About Page</h2>
          <p className="text-white/50 mt-1">Manage team, values, images & content</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              activeTab === tab.id
                ? 'bg-[#D4AF37] text-white'
                : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
            data-testid={`about-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Team Members Tab */}
      {activeTab === 'team' && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-white">Team Members ({aboutData.teamMembers?.length || 0})</h3>
            <button
              onClick={() => setEditingMember({ id: '', name: '', role: '', image: '', bio: '', fullBio: '', order: aboutData.teamMembers?.length || 0, _isNew: true })}
              className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#C4A030] transition-colors"
              data-testid="add-team-member-btn"
            >
              <Plus className="w-4 h-4" /> Add Member
            </button>
          </div>

          <div className="space-y-3">
            {(aboutData.teamMembers || []).sort((a, b) => a.order - b.order).map(member => (
              <div key={member.id} className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center gap-4">
                <img
                  src={`${member.image}`}
                  alt={member.name}
                  className="w-14 h-14 rounded-full object-cover border-2 border-white/20"
                  onError={(e) => { e.target.src = '/logo.png'; }}
                />
                <div className="flex-1 min-w-0">
                  <h4 className="text-white font-semibold">{member.name}</h4>
                  <p className="text-[#D4AF37] text-sm">{member.role}</p>
                  <p className="text-white/40 text-xs truncate mt-1">{member.bio}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => setEditingMember({ ...member })}
                    className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                    data-testid={`edit-member-${member.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteTeamMember(member.id)}
                    className="p-2 text-red-400/60 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                    data-testid={`delete-member-${member.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Edit/Add Member Modal */}
          <AnimatePresence>
            {editingMember && (
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
                onClick={() => setEditingMember(null)}
              >
                <motion.div
                  initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
                  className="bg-[#0a0875] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                  onClick={e => e.stopPropagation()}
                >
                  <div className="sticky top-0 bg-[#0a0875] border-b border-white/10 p-4 flex justify-between items-center z-10">
                    <h3 className="text-white font-bold">{editingMember._isNew ? 'Add' : 'Edit'} Team Member</h3>
                    <button onClick={() => setEditingMember(null)} className="p-1 hover:bg-white/10 rounded">
                      <X className="w-5 h-5 text-white/60" />
                    </button>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-white/60 text-sm mb-1 block">Name</label>
                        <input
                          value={editingMember.name}
                          onChange={e => setEditingMember({ ...editingMember, name: e.target.value })}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-white/60 text-sm mb-1 block">Role</label>
                        <input
                          value={editingMember.role}
                          onChange={e => setEditingMember({ ...editingMember, role: e.target.value })}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                        />
                      </div>
                    </div>
                    <ImagePicker
                      label="Team Photo"
                      value={editingMember.image}
                      onChange={(url) => setEditingMember({ ...editingMember, image: url })}
                      folder="team"
                      aspect="aspect-square"
                      testId="team-image-picker"
                    />
                    <div>
                      <label className="text-white/60 text-sm mb-1 block">Short Bio (card preview)</label>
                      <textarea
                        value={editingMember.bio}
                        onChange={e => setEditingMember({ ...editingMember, bio: e.target.value })}
                        rows={2}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-white/60 text-sm mb-1 block">Full Biography (modal popup)</label>
                      <textarea
                        value={editingMember.fullBio}
                        onChange={e => setEditingMember({ ...editingMember, fullBio: e.target.value })}
                        rows={10}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                        placeholder="Separate paragraphs with blank lines"
                      />
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                      <button
                        onClick={() => setEditingMember(null)}
                        className="px-4 py-2 text-white/60 hover:text-white border border-white/10 rounded-lg"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          const { _isNew, ...memberData } = editingMember;
                          if (!memberData.id) memberData.id = memberData.name.toLowerCase().replace(/\s+/g, '-');
                          saveTeamMember(memberData, _isNew);
                        }}
                        disabled={saving}
                        className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#C4A030] disabled:opacity-50"
                        data-testid="save-member-btn"
                      >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Save
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Values Tab */}
      {activeTab === 'values' && (
        <div className="space-y-4">
          {(aboutData.values || []).map(value => (
            <div key={value.id} className="bg-white/5 border border-white/10 rounded-xl p-4">
              {editingValue?.id === value.id ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-white/60 text-sm mb-1 block">Title</label>
                      <input
                        value={editingValue.title}
                        onChange={e => setEditingValue({ ...editingValue, title: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-white/60 text-sm mb-1 block">Icon (Sparkles, Target, Heart, Users)</label>
                      <input
                        value={editingValue.icon}
                        onChange={e => setEditingValue({ ...editingValue, icon: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-white/60 text-sm mb-1 block">Description</label>
                    <textarea
                      value={editingValue.description}
                      onChange={e => setEditingValue({ ...editingValue, description: e.target.value })}
                      rows={2}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setEditingValue(null)} className="px-3 py-1.5 text-white/60 text-sm border border-white/10 rounded-lg">Cancel</button>
                    <button
                      onClick={() => saveValue(editingValue)}
                      disabled={saving}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-[#D4AF37] text-white text-sm rounded-lg"
                    >
                      {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-white font-semibold">{value.title}</h4>
                    <p className="text-white/50 text-sm">{value.description}</p>
                    <p className="text-white/30 text-xs mt-1">Icon: {value.icon}</p>
                  </div>
                  <button
                    onClick={() => setEditingValue({ ...value })}
                    className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Cut-Arounds Tab */}
      {activeTab === 'cutarounds' && (
        <div className="space-y-4">
          <p className="text-white/40 text-sm mb-4">
            Character cut-around images placed around the site. Update the image path to change which character appears in each section.
          </p>
          {(aboutData.cutArounds || []).map(ca => (
            <CutAroundEditor key={ca.id} cutaround={ca} onSave={saveCutAround} saving={saving} />
          ))}
        </div>
      )}

      {/* Page Content Tab */}
      {activeTab === 'content' && (
        <div className="space-y-6">
          {[
            { key: 'heroTitle', label: 'Hero Title', type: 'input' },
            { key: 'heroSubtitle', label: 'Hero Subtitle', type: 'textarea' },
            { key: 'missionText', label: 'Mission Text', type: 'textarea' },
            { key: 'screenToStageTitle', label: '"From Screen to Stage" Title', type: 'input' },
            { key: 'screenToStageText', label: '"From Screen to Stage" Text', type: 'textarea' },
            { key: 'ctaTitle', label: 'CTA Title', type: 'input' },
            { key: 'ctaText', label: 'CTA Text', type: 'textarea' },
          ].map(field => (
            <div key={field.key}>
              <label className="text-white/60 text-sm mb-1 block">{field.label}</label>
              {field.type === 'input' ? (
                <input
                  value={contentForm[field.key] || ''}
                  onChange={e => setContentForm({ ...contentForm, [field.key]: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                />
              ) : (
                <textarea
                  value={contentForm[field.key] || ''}
                  onChange={e => setContentForm({ ...contentForm, [field.key]: e.target.value })}
                  rows={3}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                />
              )}
            </div>
          ))}
          <button
            onClick={saveContent}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 bg-[#D4AF37] text-white rounded-lg hover:bg-[#C4A030] disabled:opacity-50"
            data-testid="save-content-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save All Content
          </button>
        </div>
      )}
    </div>
  );
};

const CutAroundEditor = ({ cutaround, onSave, saving }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...cutaround });

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
      <div className="flex items-center gap-4">
        <img
          src={`${form.image}`}
          alt={form.alt}
          className="w-20 h-20 object-contain bg-white/5 rounded-lg"
          onError={(e) => { e.target.src = '/logo.png'; }}
        />
        <div className="flex-1">
          <h4 className="text-white font-semibold text-sm">{cutaround.section}</h4>
          {editing ? (
            <div className="space-y-3 mt-2">
              <ImagePicker
                label=""
                value={form.image}
                onChange={(url) => setForm({ ...form, image: url })}
                folder="cutarounds"
                aspect="aspect-square"
                testId={`cutaround-image-${cutaround.id}`}
              />
              <input
                value={form.alt}
                onChange={e => setForm({ ...form, alt: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm"
                placeholder="Alt text"
              />
              <div className="flex gap-2">
                <button onClick={() => setEditing(false)} className="px-3 py-1 text-white/60 text-xs border border-white/10 rounded-lg">Cancel</button>
                <button
                  onClick={() => { onSave(form); setEditing(false); }}
                  disabled={saving}
                  className="px-3 py-1 bg-[#D4AF37] text-white text-xs rounded-lg"
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-1">
              <p className="text-white/40 text-xs truncate">{cutaround.image}</p>
              <p className="text-white/30 text-xs">{cutaround.alt}</p>
            </div>
          )}
        </div>
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg"
          >
            <Edit className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default AboutManager;
